/**
 * Reset Gift Application — Applications Closed
 *
 * The 2026 Costa Rica Reset Gift application window is closed.
 * This page is retained at /reset-gift/apply so existing bookmarks, email
 * links, and ads land on a graceful closure notice instead of 404.
 *
 * If applications re-open, replace this file with the previous form
 * implementation (recoverable from git history) or revert in App.js.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, ArrowLeft } from 'lucide-react';

const GiftApplicationPage = () => {
  // Fire a lightweight Meta Pixel event so we can measure how many people
  // still arrive at this URL after applications closed — useful for retargeting
  // the next cohort. No-op if fbq isn't available.
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'GiftApplicationClosedView');
      }
    } catch (_) { /* never block render */ }
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'linear-gradient(180deg, #F4ECDF 0%, #FFFFFF 100%)' }}
      data-testid="gift-application-closed-page"
    >
      <div className="max-w-xl mx-auto text-center">
        <div
          className="inline-flex items-center justify-center h-16 w-16 rounded-full mb-6"
          style={{ backgroundColor: 'rgba(192,108,84,0.12)' }}
        >
          <Heart className="h-8 w-8" style={{ color: '#C06C54' }} />
        </div>

        <h1
          className="text-3xl sm:text-4xl mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}
          data-testid="closed-headline"
        >
          Applications Are Now Closed
        </h1>

        <p
          className="text-base mb-6 leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif", color: 'rgba(26,60,52,0.75)' }}
        >
          Thank you for your interest in the <strong>Sister Diamonds Reset
          Gift</strong>. Applications for the 2026 Costa Rica retreat are now
          closed, and we are reviewing every story submitted with deep care.
        </p>

        <p
          className="text-base mb-8 leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif", color: 'rgba(26,60,52,0.75)' }}
        >
          If you applied, watch your inbox — we will be in touch directly with
          next steps. The Sister Diamonds team is moved beyond words by the
          honesty and courage in your submissions.
        </p>

        <div
          className="rounded-xl p-6 mb-8 text-left"
          style={{
            backgroundColor: 'rgba(26,60,52,0.04)',
            border: '1px solid rgba(26,60,52,0.10)'
          }}
        >
          <p
            className="text-sm font-medium mb-2"
            style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}
          >
            Want to stay close to what's next?
          </p>
          <p
            className="text-sm"
            style={{
              fontFamily: "'Manrope', sans-serif",
              color: 'rgba(26,60,52,0.65)',
            }}
          >
            Future retreats, gift opportunities, and Inner Circle invitations
            open first to our community. Join the Sister Diamonds App to be
            on the inside the moment doors re-open.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all"
            style={{
              backgroundColor: '#1A3C34',
              color: 'white',
              fontFamily: "'Manrope', sans-serif",
            }}
            data-testid="closed-home-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sister Diamonds
          </Link>
          <a
            href="mailto:info@volitionenterprises.com"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all border"
            style={{
              borderColor: '#C06C54',
              color: '#C06C54',
              fontFamily: "'Manrope', sans-serif",
            }}
            data-testid="closed-contact-link"
          >
            <Mail className="h-4 w-4" />
            Contact the Team
          </a>
        </div>

        <p
          className="mt-10 text-xs italic"
          style={{
            color: 'rgba(26,60,52,0.45)',
            fontFamily: "'Manrope', sans-serif",
          }}
        >
          With love and reverence — Crystal, Dr. Nicole, and the Sister
          Diamonds Team.
        </p>
      </div>
    </div>
  );
};

export default GiftApplicationPage;
